import { RigFracActivityMetric, RigFracTimeScale } from "constants/charts.enums";
import { format } from "echarts";
import { removeCount } from "utils/removeCountFromTitle";

// ECharts hands tooltip formatters an untyped params bag (object or array).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TooltipParams = any;

export const tooltipRow = (label: string, value: string) =>
  `<div>${format.encodeHTML(label)}: <b>${format.encodeHTML(value)}</b></div>`;

/** `tooltipRow` as a table row, for tooltips that line labels and values up in columns. */
export const tooltipColumnRow = (label: string, value: string) =>
  `<tr><td style="color:#8c8c8c;padding-right:16px;">${format.encodeHTML(
    label
  )}:</td><td style="color:#262626;">${format.encodeHTML(value)}</td></tr>`;

// Custom ranges are bucketed into ISO weeks server-side (chart-svc
// RigFracActivityService), so they read "Week" like Weekly.
const TIME_SCALE_LABELS: Record<RigFracTimeScale, string> = {
  [RigFracTimeScale.Weekly]: "Week",
  [RigFracTimeScale.Monthly]: "Month",
  [RigFracTimeScale.Quarterly]: "Quarter",
  [RigFracTimeScale.Custom]: "Week"
};

const METRIC_LABELS: Partial<Record<RigFracActivityMetric, string>> = {
  [RigFracActivityMetric.RigCount]: "Rigs",
  [RigFracActivityMetric.FracCrewCount]: "Frac Crews",
  [RigFracActivityMetric.ApprovedPermits]: "Approved Permits"
};

export const rigFracMetricLabel = (metric: Maybe<RigFracActivityMetric>) =>
  metric ? METRIC_LABELS[metric] : undefined;

export const rigFracBucketLabel = (timeScale: Maybe<RigFracTimeScale>): string =>
  (timeScale && TIME_SCALE_LABELS[timeScale]) || "Date";

interface RigFracItemTooltipLabels {
  groupBy: string;
  metric: string;
  bucket: string;
}

/**
 * Activity vs Time tooltip, matching Insights: hovering a bar shows only that
 * group ("<group by>: <group>", "<metric>: <value> (of <bucket total>)",
 * "<bucket>: <date>"); hovering the overlay line shows "<series>: <value>" and
 * the bucket row. `bucketTotals` is precomputed from every bar series since
 * tooltip params omit legend-hidden ones. Price series (`decimalSeriesLabels`)
 * keep 2 decimals.
 */
export function buildRigFracItemTooltipFormatter(
  labels: RigFracItemTooltipLabels,
  bucketTotals: readonly number[],
  decimalSeriesLabels?: Maybe<ReadonlySet<string>>,
  lineMetricLabel?: Maybe<string>,
  hoverSeriesIdRef?: Maybe<{ current: Maybe<string> }>
): (params: TooltipParams) => string {
  return (params: TooltipParams) => {
    // Axis trigger hands over every series in the hovered bucket; show only the
    // bar/line under the pointer (set by RigFracChart's series hover), and
    // nothing when the pointer is in the column but on neither.
    const hoveredId = hoverSeriesIdRef?.current;
    if (!hoveredId) return "";
    const p = (Array.isArray(params) ? params : [params]).find(
      (r) => r?.seriesId === hoveredId
    );
    if (!p) return "";
    const raw = Array.isArray(p.value) ? p.value[1] : p.value;
    const seriesName = String(p.seriesName ?? "");
    const bucketRow = tooltipRow(labels.bucket, String(p.name ?? p.axisValueLabel ?? ""));
    if (p.seriesType === "bar") {
      const value = Math.round(Number(raw) || 0).toLocaleString();
      const total = (bucketTotals[p.dataIndex] ?? 0).toLocaleString();
      return (
        tooltipRow(labels.groupBy, seriesName) +
        tooltipRow(labels.metric, `${value} (of ${total})`) +
        bucketRow
      );
    }
    const value = decimalSeriesLabels?.has(seriesName)
      ? (Number(raw) || 0).toFixed(2)
      : Math.round(Number(raw) || 0).toLocaleString();
    return tooltipRow(lineMetricLabel ?? seriesName, value) + bucketRow;
  };
}

export const buildRecentActivityItemTooltipFormatter =
  (groupByLabel: string, metricLabel: string) =>
  (params: TooltipParams): string => {
    const p = Array.isArray(params) ? params[0] : params;
    if (!p) return "";
    const value = Math.round(Number(p.value) || 0).toLocaleString();
    return (
      tooltipRow(groupByLabel, removeCount(String(p.name ?? ""))) +
      tooltipRow(metricLabel, value)
    );
  };
